import React, {useState} from 'react';
import {Col, Row, Typography, Button} from 'antd';
import styled, {keyframes} from 'styled-components';
import ReactPlayer from 'react-player';
import {PlayCircleOutlined, PauseCircleOutlined} from '@ant-design/icons';
import Techstars from '../images/techstars.png';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const VideoContainer = styled.section`
  id: video-section;
  width: 100%;
  max-width: 100vw;
  background: ${props =>
    props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
  background-size: 400% 400%;
  animation: ${gradientShift} 20s ease infinite;
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  position: relative;
  overflow: hidden;
  margin: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.2);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: white;
    text-align: center;
    margin-bottom: ${props => props.theme.spacing?.[12] || '3rem'};
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: ${fadeInUp} 1s ease-out;

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
      margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
    }
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  overflow: hidden;
  box-shadow: ${props => props.theme.boxShadow?.['2xl'] || '0 25px 50px -12px rgba(0, 0, 0, 0.25)'};
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

  &:hover {
    transform: translateY(-5px);
  }

  .react-player {
    border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
    overflow: hidden;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${props => props.theme.spacing?.[8] || '2rem'};
  animation: ${fadeInUp} 1s ease-out 0.4s both;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
    margin-top: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const Description = styled(Paragraph)`
  && {
    color: white;
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const Divider = styled.hr`
  border: none;
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  margin: ${props => props.theme.spacing?.[6] || '1.5rem'} 0;
  border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
`;

const FeaturedText = styled.h5`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
`;

const TechstarsLogo = styled.img`
  max-width: 270px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  filter: brightness(1.1);

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: ${props => props.theme.breakpoints?.sm || '640px'}) {
    max-width: 200px;
  }
`;

const VideoSection = () => {
  const [playing, setPlaying] = useState(false);

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  return (
    <VideoContainer id="video-section">
      <ContentWrapper>
        <SectionTitle level={2}>A Platform for College Creatives to Connect</SectionTitle>

        <Row gutter={[32, 32]} align="middle">
          <Col xs={24} lg={12}>
            <VideoWrapper>
              <ReactPlayer
                url="https://youtu.be/YVB3PrS5HDg"
                width="100%"
                height="300px"
                playing={playing}
                controls={true}
                className="react-player"
                config={{
                  youtube: {
                    playerVars: {
                      showinfo: 1,
                      modestbranding: 1,
                    },
                  },
                }}
              />
            </VideoWrapper>
          </Col>

          <Col xs={24} lg={12}>
            <ContentCard>
              <Description>
                CLLCTVE is a platform connecting college creators with brands targeting Gen-Z
                consumers. We solve a big problem for brands, for students and for universities, by
                offering a talent marketplace for brands to connect with college students for paid
                creative work.
              </Description>

              <Divider />

              <FeaturedText>Featured in</FeaturedText>
              <a
                href="https://www.techstars.com/accelerators/los-angeles"
                target="_blank"
                rel="noopener noreferrer"
              >
                <TechstarsLogo src={Techstars} alt="Techstars" />
              </a>
            </ContentCard>
          </Col>
        </Row>
      </ContentWrapper>
    </VideoContainer>
  );
};

export default VideoSection;
